import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

interface Props {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
}

type Role = 'developer' | 'tester' | 'viewer';

const ROLE_OPTIONS: { value: Role; label: string; description: string; color: string }[] = [
  { value: 'developer', label: 'Developer', description: 'Can create and manage issues',    color: '#3b82f6' },
  { value: 'tester',    label: 'Tester',    description: 'Can review and test issues',     color: '#f59e0b' },
  { value: 'viewer',    label: 'Viewer',    description: 'Can view project and issues',    color: '#6b7280' },
];

export default function InviteMemberModal({ projectId, projectName, onClose, onSuccess }: Props) {
  const [email,    setEmail]    = useState('');
  const [role,     setRole]     = useState<Role>('developer');
  const [sending,  setSending]  = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;

    setSending(true);
    setFeedback(null);

    try {
      const json = await api.post<{ isDirectAdd: boolean; emailWarning?: string }>(
        '/api/invite-to-project',
        { email: trimmedEmail, role, projectId }
      );
      const msg = json.isDirectAdd
        ? `${trimmedEmail} has been added to the project.`
        : `Invitation sent to ${trimmedEmail}.`;
      setFeedback({ type: 'success', message: json.emailWarning ? `${msg} (Note: notification email failed to send.)` : msg });
      setEmail('');
      onSuccess();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Failed to send invitation.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel invite-modal" role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
          </div>
          <div>
            <h2 id="invite-modal-title" className="modal-title">Invite to Project</h2>
            <p className="modal-subtitle">{projectName}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSend}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="invite-email">Email address</label>
              <input
                id="invite-email"
                ref={emailRef}
                type="email"
                className="form-input"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <div className="invite-role-grid">
                {ROLE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`invite-role-option${role === opt.value ? ' selected' : ''}`}
                    style={role === opt.value ? { borderColor: opt.color, background: `${opt.color}10` } : {}}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={opt.value}
                      checked={role === opt.value}
                      onChange={() => setRole(opt.value)}
                      className="sr-only"
                    />
                    <span className="invite-role-dot" style={{ background: opt.color }} />
                    <span className="invite-role-label">{opt.label}</span>
                    <span className="invite-role-desc">{opt.description}</span>
                    {role === opt.value && (
                      <svg className="invite-role-check" viewBox="0 0 24 24" fill="none" stroke={opt.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {feedback && (
              <div className={`invite-feedback ${feedback.type}`}>
                {feedback.type === 'success' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
                {feedback.message}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={sending || !email.trim()}>
              {sending ? (
                <><span className="btn-spinner" /> Sending…</>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
